import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Users, UserPlus, Check, X, Clock, Search, Rss, Heart, SlidersHorizontal, MapPin } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Loading, EmptyState } from "@/components/common/Loading";
import { MessageButton } from "@/components/social/ProfileActions";
import { MutualFriendsLink } from "@/components/social/MutualFriendsModal";
import { useRealtimeEvent } from "@/context/RealtimeContext";
import { useReferences, labelFor } from "@/hooks/useReferences";
import { DirectoryFilters, DIR_FILTERS } from "@/components/directory/DirectoryFilters";
import { isRegionCenter } from "@/components/directory/GeoRadiusFilter";

// --- Lightweight date helpers for relationship metadata (Issue 8) ---------
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthYear = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};
const dateTs = (x) => {
  const v = x?.since || x?.created_at || x?.accepted_at;
  const t = v ? new Date(v).getTime() : 0;
  return isNaN(t) ? 0 : t;
};
const sortByDate = (list, order) => {
  const dir = order === "oldest" ? 1 : -1;
  return [...(list || [])].sort((a, b) => dir * (dateTs(a) - dateTs(b)));
};

function PersonRow({ person, right, meta }) {
  const inner = (
    <div className="flex items-center gap-3">
      <Avatar className="h-11 w-11">
        {person.headshot_url && <AvatarImage src={fileUrl(person.headshot_url)} alt="" />}
        <AvatarFallback className="bg-teal-50 text-teal-700">{(person.display_name || "?")[0]}</AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <div className="truncate font-[600] text-foreground">{person.display_name}</div>
        {person.headline && <div className="truncate text-sm text-muted-foreground">{person.headline}</div>}
      </div>
    </div>
  );
  return (
    <Card className="flex items-center justify-between gap-3 p-4" data-testid={`person-row-${person.user_id}`}>
      <div className="min-w-0 flex-1">
        {person.slug ? <Link to={`/p/${person.slug}`} className="block hover:opacity-80">{inner}</Link> : inner}
        {meta && <div className="mt-1.5 pl-14">{meta}</div>}
      </div>
      <div className="flex shrink-0 items-center gap-2">{right}</div>
    </Card>
  );
}

const profileTo = (c) =>
  c.target_type === "capital" ? `/capital/${c.slug}` : c.target_type === "business" ? `/b/${c.slug}` : `/p/${c.slug}`;

function ProfileRow({ item }) {
  const isPersonal = item.target_type === "personal";
  const inner = (
    <div className="flex items-center gap-3">
      {isPersonal ? (
        <Avatar className="h-11 w-11">
          {item.image_url && <AvatarImage src={fileUrl(item.image_url)} alt="" />}
          <AvatarFallback className="bg-teal-50 text-teal-700">{(item.display_name || "?")[0]}</AvatarFallback>
        </Avatar>
      ) : (
        <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden bg-secondary">
          {item.image_url ? <img src={fileUrl(item.image_url)} alt="" className="h-full w-full object-cover" /> : <span className="font-[600] text-muted-foreground">{(item.display_name || "?")[0]}</span>}
        </div>
      )}
      <div className="min-w-0">
        <div className="truncate font-[600] text-foreground">{item.display_name}</div>
        {item.headline && <div className="truncate text-sm text-muted-foreground">{item.headline}</div>}
        {monthYear(item.since) && <div className="truncate text-xs text-muted-foreground" data-testid={`profile-since-${item.target_id}`}>Since {monthYear(item.since)}</div>}
      </div>
    </div>
  );
  return (
    <Card className="flex items-center justify-between gap-3 p-4" data-testid={`profile-row-${item.target_id}`}>
      {item.slug ? <Link to={profileTo(item)} className="min-w-0 flex-1 hover:opacity-80">{inner}</Link> : <div className="min-w-0 flex-1">{inner}</div>}
      <Badge variant="secondary" className="shrink-0 capitalize">{item.target_type}</Badge>
    </Card>
  );
}

function DateSortControl({ value, onChange, testId }) {
  return (
    <div className="flex justify-end">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-[150px]" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="newest">Newest</SelectItem>
          <SelectItem value="oldest">Oldest</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function Section({ title, items, empty, children }) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-[640] uppercase tracking-wide text-muted-foreground">{title}</h3>
      {(!items || items.length === 0) ? empty : <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Friends search panel — same filter UX as the public People directory but
// scoped to the signed-in user's accepted connections.
// ---------------------------------------------------------------------------

const emptyFilters = () => ({ multi: {}, sort: "recent", geoOn: false, center: null, radius: 50 });
const loc = (l) => [l?.city, l?.state].filter(Boolean).join(", ");

function FriendResultCard({ it, references, acceptedAt }) {
  const since = monthYear(acceptedAt);
  return (
    <Link to={`/p/${it.slug}`}>
      <Card className="flex h-full flex-col border-0 p-4" data-testid={`network-search-card-${it.slug}`}>
        <div className="flex items-center gap-3">
          <Avatar className="h-12 w-12 shrink-0">
            {it.headshot_url && <AvatarImage src={fileUrl(it.headshot_url)} className="object-cover" />}
            <AvatarFallback className="bg-teal-50 text-teal-700">{(it.display_name || "U")[0]}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="font-[640] text-foreground break-words">{it.display_name}</div>
            {it.handle && <div className="text-sm text-primary">@{it.handle}</div>}
          </div>
        </div>
        {it.headline && <p className="mt-3 line-clamp-2 text-sm text-foreground">{it.headline}</p>}
        {loc(it.location) && <div className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {loc(it.location)}</div>}
        {since && <div className="mt-2 text-xs text-muted-foreground" data-testid={`network-connected-since-${it.slug}`}>Connected {since}</div>}
        {(it.industries || []).length > 0 && (
          <div className="mt-auto flex flex-wrap gap-1.5 pt-3">
            {(it.industries || []).slice(0, 3).map((i) => <Badge key={i} variant="secondary" className="bg-secondary">{labelFor(references.industries, i)}</Badge>)}
            {(it.industries || []).length > 3 && <Badge variant="secondary" className="bg-secondary">+{(it.industries || []).length - 3}</Badge>}
          </div>
        )}
      </Card>
    </Link>
  );
}

function FriendsSearchPanel({ connectionUserIds, acceptedAtById }) {
  const { references } = useReferences();
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState(emptyFilters);
  const [items, setItems] = useState(null);
  const groups = useMemo(() => DIR_FILTERS.people, []);

  const patch = useCallback((p) => setFilters((f) => ({ ...f, ...p })), []);
  const clearAll = useCallback(() => setFilters((f) => ({ multi: {}, sort: "recent", geoOn: false, center: f.center, radius: 50 })), []);

  useEffect(() => {
    if (!filters.center && filters.sort === "nearest") setFilters((f) => ({ ...f, sort: "recent" }));
  }, [filters.center, filters.sort]);

  // Debounce free-text search separately from filter changes.
  const [dq, setDq] = useState("");
  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 300); return () => clearTimeout(t); }, [q]);

  const multiKey = JSON.stringify(filters.multi);
  const centerKey = filters.center ? `${filters.center.lat},${filters.center.lng}` : "";
  const idsKey = (connectionUserIds || []).join(",");

  useEffect(() => {
    // No connections at all → nothing to render.
    if (!connectionUserIds || connectionUserIds.length === 0) { setItems([]); return undefined; }
    const controller = new AbortController();
    let cancelled = false;
    const params = { user_ids: idsKey };
    if (dq) params.q = dq;
    groups.forEach((g) => { const arr = filters.multi[g.key]; if (arr && arr.length) params[g.key] = arr.join(","); });
    let sort = filters.sort;
    if (filters.geoOn && filters.center) {
      const c = filters.center;
      if (isRegionCenter(c)) {
        if (c.granularity === "state") params.state = c.state_code;
        else params.country = c.country_code;
      } else {
        params.lat = c.lat; params.lng = c.lng; params.radius = filters.radius;
      }
    } else if (sort === "nearest") {
      sort = "recent";
    }
    // Connection-date sorts are applied client-side (see displayItems); use a
    // neutral backend sort so search/filter results are unchanged.
    if (sort === "connected_recent" || sort === "connected_longest") sort = "recent";
    params.sort = sort;
    setItems(null);
    (async () => {
      try {
        const { data } = await api.get("/directory/people", { params, signal: controller.signal });
        if (!cancelled) setItems(data);
      } catch (e) {
        if (!cancelled && e?.code !== "ERR_CANCELED" && e?.name !== "CanceledError") setItems([]);
      }
    })();
    return () => { cancelled = true; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, dq, multiKey, filters.sort, filters.geoOn, filters.radius, centerKey]);

  const chips = useMemo(() => {
    const out = [];
    groups.forEach((g) => (filters.multi[g.key] || []).forEach((v) => out.push({ key: g.key, value: v, label: labelFor(references[g.ref], v) })));
    if (filters.geoOn && filters.center) out.push({ key: "__geo", value: "geo", label: isRegionCenter(filters.center) ? `In ${filters.center.label}` : `Within ${filters.radius} mi of ${filters.center.label}` });
    return out;
  }, [filters, groups, references]);

  const removeChip = (chip) => {
    if (chip.key === "__geo") { patch({ geoOn: false }); return; }
    patch({ multi: { ...filters.multi, [chip.key]: (filters.multi[chip.key] || []).filter((v) => v !== chip.value) } });
  };

  // Client-side reorder by connection date for the "connected_*" sorts.
  const displayItems = useMemo(() => {
    if (!items) return items;
    if (filters.sort !== "connected_recent" && filters.sort !== "connected_longest") return items;
    const dir = filters.sort === "connected_longest" ? 1 : -1;
    const ts = (id) => { const v = acceptedAtById?.[id]; const n = v ? new Date(v).getTime() : 0; return isNaN(n) ? 0 : n; };
    return [...items].sort((a, b) => dir * (ts(a.user_id) - ts(b.user_id)));
  }, [items, filters.sort, acceptedAtById]);

  const filterPanel = <DirectoryFilters kind="people" value={filters} onChange={patch} onClear={clearAll} hasActive={chips.length > 0} />;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12" data-testid="network-search-panel">
      <aside className="hidden lg:col-span-3 lg:block">
        <div className="lg:sticky lg:top-20">{filterPanel}</div>
      </aside>

      <div className="lg:col-span-9">
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[200px] flex-1">
            <Input
              placeholder="Search your network…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="network-search-input"
            />
          </div>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="secondary" className="lg:hidden" data-testid="network-mobile-filters"><SlidersHorizontal className="mr-1.5 h-4 w-4" /> Filters</Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[320px] overflow-y-auto">{filterPanel}</SheetContent>
          </Sheet>
          <Select value={filters.sort} onValueChange={(v) => patch({ sort: v })}>
            <SelectTrigger className="w-[170px]" data-testid="network-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name (A–Z)</SelectItem>
              <SelectItem value="name_desc">Name (Z–A)</SelectItem>
              <SelectItem value="recent">Newest Added</SelectItem>
              <SelectItem value="connected_recent">Recently connected</SelectItem>
              <SelectItem value="connected_longest">Longest connected</SelectItem>
              <SelectItem value="nearest" disabled={!filters.center}>Nearest</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {chips.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {chips.map((c, i) => (
              <button key={`${c.key}-${c.value}-${i}`} onClick={() => removeChip(c)} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-3 py-1 text-xs font-[500] text-foreground transition-colors hover:bg-secondary" data-testid={`network-chip-${c.value}`}>
                {c.label}<X className="h-3 w-3" />
              </button>
            ))}
            <button onClick={clearAll} className="text-xs font-[540] text-primary hover:underline">Clear all</button>
          </div>
        )}

        {items !== null && (
          <div className="mt-4 text-sm text-muted-foreground" data-testid="network-search-count">
            {items.length} friend{items.length !== 1 ? "s" : ""} match your search
          </div>
        )}

        {items === null ? (
          <div className="mt-4"><Loading /></div>
        ) : items.length === 0 ? (
          <div className="mt-4">
            <EmptyState
              icon={Users}
              title={connectionUserIds.length === 0 ? "No connections yet" : "No friends match these filters"}
              description={connectionUserIds.length === 0
                ? "Connect with members from their profiles to grow your network."
                : "Try removing a filter or widening your search."}
              action={connectionUserIds.length === 0
                ? <Link to="/people"><Button>Browse people</Button></Link>
                : <button onClick={clearAll} className="text-primary hover:underline">Clear all filters</button>}
              testId="network-search-empty"
            />
          </div>
        ) : (
          <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {displayItems.map((it) => <FriendResultCard key={it.slug || it.user_id} it={it} references={references} acceptedAt={acceptedAtById?.[it.user_id]} />)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Network() {
  const [searchParams] = useSearchParams();
  const initialTab = ["friends", "following", "fans"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "friends";
  const [tab, setTab] = useState(initialTab);
  const [connections, setConnections] = useState(null);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [following, setFollowing] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [fanOf, setFanOf] = useState([]);
  const [fans, setFans] = useState([]);
  const [followingSort, setFollowingSort] = useState("newest");
  const [fansSort, setFansSort] = useState("newest");

  const load = async () => {
    try {
      const [c, r, fMine, fIn, faMine, faIn] = await Promise.all([
        api.get("/network/connections"),
        api.get("/network/requests"),
        api.get("/network/relationships/mine", { params: { kind: "follow" } }),
        api.get("/network/relationships/inbound", { params: { kind: "follow" } }),
        api.get("/network/relationships/mine", { params: { kind: "fan" } }),
        api.get("/network/relationships/inbound", { params: { kind: "fan" } }),
      ]);
      setConnections(c.data);
      setRequests(r.data);
      setFollowing(fMine.data || []);
      setFollowers(fIn.data || []);
      setFanOf(faMine.data || []);
      setFans(faIn.data || []);
    } catch (e) { setConnections([]); }
  };
  useEffect(() => { load(); }, []);
  useRealtimeEvent((evt) => { if (evt.type === "notification") load(); });

  const connectionUserIds = useMemo(
    () => (connections || []).map((c) => c.user_id).filter(Boolean),
    [connections],
  );
  const acceptedAtById = useMemo(() => {
    const m = {};
    (connections || []).forEach((c) => { if (c.user_id) m[c.user_id] = c.accepted_at || c.since; });
    return m;
  }, [connections]);
  const followingSorted = useMemo(() => sortByDate(following, followingSort), [following, followingSort]);
  const followersSorted = useMemo(() => sortByDate(followers, followingSort), [followers, followingSort]);
  const fanOfSorted = useMemo(() => sortByDate(fanOf, fansSort), [fanOf, fansSort]);
  const fansSorted = useMemo(() => sortByDate(fans, fansSort), [fans, fansSort]);

  const respond = async (id, action) => {
    try { await api.post("/network/respond", { connection_id: id, action }); toast.success(action === "accept" ? "Connected" : "Declined"); load(); }
    catch (e) { toast.error("Action failed"); }
  };
  const withdraw = async (id) => {
    try { await api.delete(`/network/${id}`); toast.success("Request withdrawn"); load(); }
    catch (e) { toast.error("Action failed"); }
  };
  const removeConn = async (id) => {
    try { await api.delete(`/network/${id}`); toast.success("Connection removed"); load(); }
    catch (e) { toast.error("Action failed"); }
  };

  if (!connections) return <Loading />;
  const incomingCount = requests.incoming.length;

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-[680] tracking-[-0.01em] text-foreground">Network</h1>
          <p className="mt-1 text-muted-foreground">Friends, the profiles you follow, and your fans.</p>
        </div>
        <Link to="/people"><Button variant="secondary" data-testid="network-find-people"><Search className="mr-2 h-4 w-4" /> Find people</Button></Link>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList>
          <TabsTrigger value="friends" data-testid="tab-friends">
            Friends ({connections.length}){incomingCount > 0 && <Badge className="ml-2 bg-primary text-primary-foreground">{incomingCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="following" data-testid="tab-following">Following ({following.length})</TabsTrigger>
          <TabsTrigger value="fans" data-testid="tab-fans">Fans ({fanOf.length})</TabsTrigger>
        </TabsList>

        {/* FRIENDS — mutual connections + pending requests */}
        <TabsContent value="friends" className="mt-4 space-y-6">
          {(incomingCount > 0 || requests.outgoing.length > 0) && (
            <div className="space-y-6">
              <Section title="Incoming requests" items={requests.incoming} empty={<EmptyState icon={UserPlus} title="No incoming requests" testId="incoming-empty" />}>
                {requests.incoming.map((p) => (
                  <PersonRow key={p.connection_id} person={p} right={
                    <>
                      <Button size="sm" onClick={() => respond(p.connection_id, "accept")} data-testid={`accept-${p.connection_id}`}><Check className="mr-1.5 h-4 w-4" /> Accept</Button>
                      <Button size="sm" variant="secondary" onClick={() => respond(p.connection_id, "decline")} data-testid={`decline-${p.connection_id}`}>Decline</Button>
                    </>
                  } />
                ))}
              </Section>
              {requests.outgoing.length > 0 && (
                <Section title="Sent requests" items={requests.outgoing} empty={null}>
                  {requests.outgoing.map((p) => (
                    <PersonRow key={p.connection_id} person={p} right={
                      <Button size="sm" variant="secondary" onClick={() => withdraw(p.connection_id)} data-testid={`withdraw-${p.connection_id}`}><Clock className="mr-1.5 h-4 w-4" /> Withdraw</Button>
                    } />
                  ))}
                </Section>
              )}
            </div>
          )}

          {connections.length === 0 ? (
            <EmptyState icon={Users} title="No connections yet" description="Connect with members from their profiles to grow your network." action={<Link to="/people"><Button>Browse people</Button></Link>} testId="connections-empty" />
          ) : (
            <>
              {/* Search & filter the user's connections using the same UX as the People directory. */}
              <FriendsSearchPanel connectionUserIds={connectionUserIds} acceptedAtById={acceptedAtById} />

              <Section title="Manage your friends" items={connections} empty={null}>
                {connections.map((c) => (
                  <PersonRow key={c.connection_id} person={c}
                    meta={<MutualFriendsLink targetUserId={c.user_id} targetName={c.display_name} count={c.mutual_count || 0} className="text-xs" />}
                    right={
                    <>
                      <MessageButton targetUserId={c.user_id} variant="secondary" iconOnly />
                      <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => removeConn(c.connection_id)} data-testid={`remove-conn-${c.connection_id}`}><X className="h-4 w-4" /></Button>
                    </>
                  } />
                ))}
              </Section>
            </>
          )}
        </TabsContent>

        {/* FOLLOWING — profiles I follow + members following me */}
        <TabsContent value="following" className="mt-4 space-y-6">
          <DateSortControl value={followingSort} onChange={setFollowingSort} testId="following-sort-select" />
          <Section title="Profiles you follow" items={followingSorted} empty={<EmptyState icon={Rss} title="Not following anyone yet" description="Follow people, businesses and capital to keep up with their updates." testId="following-empty" />}>
            {followingSorted.map((it) => <ProfileRow key={`${it.target_type}-${it.target_id}`} item={it} />)}
          </Section>
          <Section title="Members following you" items={followersSorted} empty={<EmptyState icon={Users} title="No followers yet" testId="followers-empty" />}>
            {followersSorted.map((p) => <PersonRow key={p.user_id} person={p} meta={monthYear(p.since) && <span className="text-xs text-muted-foreground">Since {monthYear(p.since)}</span>} right={<MessageButton targetUserId={p.user_id} variant="secondary" iconOnly />} />)}
          </Section>
        </TabsContent>

        {/* FANS — profiles I'm a fan of + my fans */}
        <TabsContent value="fans" className="mt-4 space-y-6">
          <DateSortControl value={fansSort} onChange={setFansSort} testId="fans-sort-select" />
          <Section title="Profiles you're a fan of" items={fanOfSorted} empty={<EmptyState icon={Heart} title="Not a fan of anyone yet" description="Tap the Fan button on profiles you love." testId="fanof-empty" />}>
            {fanOfSorted.map((it) => <ProfileRow key={`${it.target_type}-${it.target_id}`} item={it} />)}
          </Section>
          <Section title="Your fans" items={fansSorted} empty={<EmptyState icon={Heart} title="No fans yet" testId="fans-empty" />}>
            {fansSorted.map((p) => <PersonRow key={p.user_id} person={p} meta={monthYear(p.since) && <span className="text-xs text-muted-foreground">Since {monthYear(p.since)}</span>} right={<MessageButton targetUserId={p.user_id} variant="secondary" iconOnly />} />)}
          </Section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
